import React, { useCallback, useEffect, useRef, useState } from "react";
import { Animated, Modal as SmallModal, Platform, Dimensions } from "react-native";
import { useDispatch, useSelector } from 'react-redux';
import { View, Text } from '@constants/Themed';
import styles from "@stylesheet";
import LottieView from '@utils/lottieAnimation';
import { Spinner } from 'tamagui';
import { CLEAR_COMPANY, HIDE_ALL_MODALS, HIDE_LOADING_MODAL, RESET_APP, SWITCH_COMPANY_REQUEST, UPDATE_USER } from "@reduxLocal/actions/types";
import { DefaultRootState, store } from "@reduxLocal/persistState";
import apis from "@api/api";
import { PURGE } from "redux-persist";
import { useNavigation, useFocusEffect, useIsFocused } from "@react-navigation/native";

const LoadingScreen = ({ route }) => {
  const [progress, setProgress] = useState(0);
  const [text, setText] = useState("");
  //TODO animate progress state, shared Value?
  const user = useSelector((state: DefaultRootState) => state.promiseData.data?.userData);
  const { width, height } = Dimensions.get('window');
const dispatch = useDispatch();
  const navigation = useNavigation<any>();
  const isFocused = useIsFocused();

  // Helper function to safely navigate
  const safeNavigate = (screenName: string, params?: any) => {
    if (isFocused && navigation) {
      try {
        // Check if we're on /learn route and preserve learning mode
        let navigationParams = params || {};
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const isLearnRoute = window.location.pathname.includes('/learn');
          const urlParams = new URLSearchParams(window.location.search);
          const hasLearningParam = urlParams.get('learning') === 'true' || urlParams.get('learn') === 'true';
          
          if (isLearnRoute || hasLearningParam) {
            navigationParams = {
              ...navigationParams,
              learningMode: true,
            };
            // Use Learn screen instead of Login if on /learn route
            if (screenName === 'Login' && isLearnRoute) {
              screenName = 'Learn';
            }
          }
        }
        
        // Map screen names for web
        // On web, both Login and Learn go to Auth stack
        if (Platform.OS === 'web') {
          if (screenName === 'Login' || screenName === 'Learn') {
            // Navigate to the specific screen within Auth stack
            // Use replace to prevent back navigation issues
            try {
              // Only pass params if we're navigating to Learn with learningMode
              const finalParams = (screenName === 'Learn' && navigationParams.learningMode) 
                ? navigationParams 
                : {}; // Don't pass params for Login to avoid query params in URL
              
              navigation.navigate('Auth' as never, {
                screen: screenName,
                params: finalParams,
              } as any);
              
              // Update URL to match - clean URL for Login, /learn for Learn
              if (typeof window !== 'undefined') {
                const newUrl = new URL(window.location.href);
                if (screenName === 'Learn') {
                  newUrl.pathname = '/learn';
                  // Remove any learningMode query params
                  newUrl.searchParams.delete('learningMode');
                  newUrl.searchParams.delete('learning');
                  newUrl.searchParams.delete('learn');
                } else if (screenName === 'Login') {
                  newUrl.pathname = '/login';
                  // Remove any learningMode query params
                  newUrl.searchParams.delete('learningMode');
                  newUrl.searchParams.delete('learning');
                  newUrl.searchParams.delete('learn');
                }
                window.history.replaceState({}, '', newUrl.toString());
              }
              return;
            } catch (navError) {
              console.error('Navigation error in Auth stack:', navError);
              // Fallback: try direct navigation without params
              navigation.navigate('Auth' as never, screenName === 'Learn' ? { learningMode: true } : {});
              return;
            }
          }
        }
        navigation.navigate(screenName as never, navigationParams);
      } catch (error) {
        console.error('Navigation error:', error);
        // Fallback for web navigation structure
        if (Platform.OS === 'web') {
          if (screenName === 'Login' || screenName === 'Learn') {
            const isLearnRoute = typeof window !== 'undefined' && window.location.pathname.includes('/learn');
            navigation.navigate('Auth' as never, isLearnRoute ? { learningMode: true } : {});
          } else if (screenName === 'Dashboard') {
            navigation.navigate('Dashboard' as never);
          }
        }
      }
    }
  };

  useEffect(() => {
    // Reduced timeout for faster fallback navigation
    const screenTimeout = setTimeout(() => {
      console.warn('Loading screen timeout - navigating to login');
      // Check if we should navigate to Learn instead of Login
      const isLearnRoute = Platform.OS === 'web' && typeof window !== 'undefined' && window.location.pathname.includes('/learn');
      if (isLearnRoute) {
        safeNavigate('Learn', { learningMode: true });
      } else {
        safeNavigate('Login', {});
      }
    }, 10000); // Reduced from 30s to 10s
    const unsubscribe = store.subscribe(() => {
      if (store.getState().lastAction?.type === "USER COMPANY CHANGED" || store.getState().lastAction?.type === "USER COMPANY CHANGE REQUESTED" ||  store.getState().lastAction?.type === "CLEARED COMPANY") {
        console.log("User company changed, navigating to Dashboard");
        safeNavigate('Dashboard');
        dispatch({ type: HIDE_ALL_MODALS })
        unsubscribe();
      }
    });

    return () => {
      clearTimeout(screenTimeout);
      unsubscribe();
    }
  }, [isFocused]);

  const updateProgress = (progress, stateText) => {
    return new Promise<void>((resolve, reject) => {
      setProgress(progress);
      setText(stateText);
      // Reduced delay for faster navigation
      setTimeout(() => { 
        resolve(); // ✅ Now properly resolves the promise
      }, Platform.OS === 'web' ? 100 : 500); // Faster on web
    });
  }

  useEffect(() => {
    async function fetchSession() {
      try {
        // Show loading immediately when session detection starts
        await updateProgress(0.1, 'Detecting session...');
        const session = await apis.getSession();

        //Apply company or target user stored in session as default
        // PRIORITY: companyNumber > targetUserId > personal account
        if (session?.data?.company) {
          const item = session.data.company;
          dispatch({ type: SWITCH_COMPANY_REQUEST, payload: { id: item._id, logo: item.logo, companyName: item.company_name || item.copmany_name } });
        } else if (session?.data?.targetUser) {
          // Target user context - clear company selection (target user is personal account mode)
          dispatch({ type: CLEAR_COMPANY });
        } else {
          // Personal account - ensure company is cleared
          dispatch({ type: CLEAR_COMPANY });
        }

        await updateProgress(0.3, 'Loading session');
        const { isAuthenticated } = session?.data || {};

        await updateProgress(0.7, 'Loading user');

        if (isAuthenticated && user) {
          // Check TC modal state before navigating
          const tcModalVisible = store.getState()?.modals?.tcModal?.modalDisplayed;
          console.log('[LoadingScreen] TC modal state:', tcModalVisible);
          
          if (tcModalVisible) {
            console.log('[LoadingScreen] TC modal visible, blocking navigation to Dashboard');
            return;
          }

          // Fetch companies if not already present in user data
          const companiesData = user.companies;
          if (!companiesData || !Array.isArray(companiesData) || companiesData.length === 0) {
            console.log("📋 Companies not found in user data, fetching companies...");
            try {
              const companiesResponse = await apis.getUserCompanies();
              if (companiesResponse?.success && companiesResponse?.companies) {
                dispatch({
                  type: UPDATE_USER,
                  payload: { companies: companiesResponse.companies }
                });
                console.log("✅ Companies fetched and updated:", companiesResponse.companies.length);
              }
            } catch (companiesError) {
              console.warn("⚠️ Failed to fetch companies (continuing anyway):", companiesError);
              // Continue even if companies fetch fails
            }
          } else {
            console.log("✅ Companies already in user data:", companiesData.length);
          }

          console.log("[LoadingScreen] User is authenticated, loading dashboard");
          await updateProgress(1, 'Loading Dashboard');
          safeNavigate('Dashboard');
        } else {
          console.log("User not authenticated, navigating to login");
          dispatch({ type: PURGE });
          dispatch({ type: RESET_APP });
          await updateProgress(1, 'Redirecting to login');
          // Check if we're on /learn route
          const isLearnRoute = Platform.OS === 'web' && typeof window !== 'undefined' && window.location.pathname.includes('/learn');
          if (isLearnRoute) {
            // Navigate to Learn screen with learningMode param
            safeNavigate('Learn', { learningMode: true });
          } else {
            safeNavigate('Login', {});
          }
        }
      } catch (error) {
        console.warn("Error fetching session:", error);
        await updateProgress(1, "No session found");
        // Check if we're on /learn route
        const isLearnRoute = Platform.OS === 'web' && typeof window !== 'undefined' && window.location.pathname.includes('/learn');
        if (isLearnRoute) {
          // Navigate to Learn screen with learningMode param
          safeNavigate('Learn', { learningMode: true });
        } else {
          safeNavigate('Login', {});
        }
        //TODO Check cookie patch
        if (Platform.OS === 'web') {
          document.cookie = 'connect.sid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        }
        dispatch({ type: RESET_APP });
      }
    };

    if (!route?.params?.params?.flagHold) {
      fetchSession();
    }
  }, []);

  return (
    <View style={{ flex: 1,  overflow: 'visible',  }}>
      {Platform.OS != 'web' ?
        <View style={[styles.background, { position: 'absolute', minWidth: '250%', }]}>
          <LottieView resizeMode="cover" style={{ alignSelf: 'stretch', overflow: 'visible', width: width * 2.4, height: height * 1.1, }} source={require('@assets/animations/backgroundLottie.json')} autoPlay loop={true}/>
        </View>
        :
        <View style={[styles.background, { position: 'absolute', width: '200%', overflow: 'hidden' }]}>
          <LottieView resizeMode="cover" source={require('@assets/animations/backgroundLottie.json')} autoPlay loop={true}/>
        </View>
      }
      <Animated.View style={{ flex: 1, opacity: 1, margin: 100, top: Dimensions.get('window').height / 6.5, position: 'absolute', maxWidth: Platform.OS === 'web' ? 400 : '70%', justifyContent: 'center', alignContent: 'center', alignItems: 'center', alignSelf: 'center' }}>
        <LottieView style={{ flex: 1, minWidth: Platform.OS === 'web' ? '70%' : 250, minHeight: 300 }} source={require('@assets/animations/landing_Animation_dark.json')} autoPlay loop={false} />
        <View style={{ width: '50%', margin: 50 }}>
          <Spinner size="large" color={'rgb(90,192,234)'} style={{ alignContent: 'center', alignItems: 'center', alignSelf: 'center' }} />
        </View>
      </Animated.View>
    </View>
  )
}

export default LoadingScreen;